import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

import { GeoJSON, FeatureGroup, Tooltip } from 'react-leaflet';

import { ProbeMarker, Leaflet } from 'components/Leaflet';
import { useEvents, useOrganizations, useDevices } from 'hooks';
import { DashboardHelpers } from 'utils';

const DashboardFieldMap = ({
  telemetryAverages,
  telemetryPeriod,
  selectedField,
  selectedProperty,
  selectedFieldDevices,
  fields,
}) => {
  const { events, fetchEvents } = useEvents();
  const { t } = useTranslation();
  const { selectedOrganization } = useOrganizations();
  const { selectedDevices } = useDevices();
  const [filterCleared, setFilterCleared] = useState(false);
  let activeBounds = [];

  useEffect(() => {
    if (!filterCleared) {
      fetchEvents();
      setFilterCleared(true);
    }
  }, []);

  const activeDevices = _.filter(selectedDevices, (d) => {
    return !d.is_inactive;
  });

  const renderDevices =
    (selectedProperty?.id && selectedField?.id) || (!selectedOrganization && !selectedOrganization?.id)
      ? selectedFieldDevices
      : activeDevices;

  const getFieldTooltip = (field) => {
    return (
      <div>
        <div className="tooltip-header">{t('field')}</div>
        <div className="tooltip-field" style={{ marginBottom: 4, marginTop: -10 }}>
          <b>{field.name}</b>
        </div>
        <div className="tooltip-field">
          <b style={{ marginRight: 10, width: 65 }}>{t('area')}:</b>
          {field.area + ' ' + t('hectares')}
        </div>
      </div>
    );
  };

  const getFieldGeoJSON = (field) => {
    return (
      <GeoJSON data={field.geometry} key={field.id}>
        <Tooltip sticky>{getFieldTooltip(field)}</Tooltip>
      </GeoJSON>
    );
  };

  const renderFields = () => {
    const fieldLayers = [];
    if (selectedField?.id) {
      fieldLayers.push(getFieldGeoJSON(selectedField));
    } else if (fields) {
      _.forEach(fields, (field) => {
        if (field.geometry) {
          fieldLayers.push(getFieldGeoJSON(field));
        }
      });
    }
    return fieldLayers;
  };

  const renderProbeMarkers = (probes) => {
    const probeMarkers = [];
    const selectProbe = (id, toggle) => {
      if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
        console.log('SelectedProbe', id, toggle);
      }
      //TODO: Not sure why selecting it does nothing.
    };

    _.forEach(probes, (probe) => {
      const selected = selectedField ? probe.field_id === selectedField.id : false;
      const probeEvents = _.find(events, (value, key) => key === probe.id);
      let newEvents = _.filter(probeEvents, (event) => !event.read_at);
      const missingReadings = false;
      if (probe?.location?.coordinates) {
        probeMarkers.push(
          <ProbeMarker
            key={`${probe.id}_probe`}
            missingReadings={missingReadings}
            telemetry={_.get(telemetryAverages, [probe.hw_addr, telemetryPeriod])}
            id={probe.id}
            probe={probe}
            events={newEvents}
            selected={selected}
            onClick={() => selectProbe(probe.id, false)}
            position={{
              lat: Number(probe.location.coordinates[1]),
              lon: Number(probe.location.coordinates[0]),
            }}
          />
        );
      }
    });
    return probeMarkers;
  };

  const deviceBounds = (probes) => {
    const bounds = [];
    _.forEach(probes, (probe) => {
      if (probe?.location?.coordinates) {
        bounds.push([probe.location.coordinates[1], probe.location.coordinates[0]]);
      }
    });
    return bounds;
  };

  if (renderDevices && renderProbeMarkers(renderDevices).length >= 1) {
    if (selectedField && fields) {
      activeBounds = deviceBounds(renderDevices).concat(DashboardHelpers.getBoundsForField(selectedField, fields));
    } else {
      activeBounds = deviceBounds(renderDevices);
    }
  } else {
    activeBounds = DashboardHelpers.getBoundsForField(selectedField, fields);
  }

  return (
    <Leaflet key="dashboardMap" bounds={activeBounds} heightOffset={308}>
      <FeatureGroup>{renderProbeMarkers(renderDevices)}</FeatureGroup>
      {!!selectedProperty && <FeatureGroup>{renderFields()}</FeatureGroup>}
    </Leaflet>
  );
};

DashboardFieldMap.propTypes = {
  setSelectedField: PropTypes.func.isRequired,
  selectedProperty: PropTypes.object,
  selectedField: PropTypes.object,
  selectedFieldDevices: PropTypes.array,
  telemetryAverages: PropTypes.object,
  telemetryPeriod: PropTypes.number,
  fields: PropTypes.array,
  selectedDevices: PropTypes.array,
};

DashboardFieldMap.defaultProps = {
  selectedField: {},
  selectedFieldDevices: [],
  selectedProperty: {},
  fields: [],
  selectedDevices: [],
  telemetryAverages: undefined,
  telemetryPeriod: 0,
};

export default DashboardFieldMap;
